import type { BookChangePacket } from '../../types';
import type { MessageCallbacks } from './types';
import { normalizeTimestamp } from './types';

export function handleBookChanges(obj: any, cb: MessageCallbacks): boolean {
  if (!(obj?.kind === 'BOOK_CHANGES' && Array.isArray(obj.changes))) return false;

  const normalizedTimestamp = normalizeTimestamp(obj.timestamp);
  const normalizedSecId = (obj.secId || '').toUpperCase();

  const normalizedPacket: BookChangePacket = {
    kind: 'BOOK_CHANGES',
    secId: normalizedSecId,
    symbol: obj.symbol,
    timestamp: normalizedTimestamp,
    changes: obj.changes,
  };

  cb.setBookChanges(prev => {
    const exists = prev.some(pkt =>
      pkt.timestamp === normalizedTimestamp &&
      pkt.secId === normalizedSecId &&
      pkt.changes.length === normalizedPacket.changes.length
    );
    if (exists) return prev;
    return [normalizedPacket, ...prev];
  });

  if (normalizedSecId) {
    cb.lastUpdateBySecIdRef.current.set(normalizedSecId, normalizedTimestamp);
    if (normalizedSecId === cb.selectedSymbol.toUpperCase()) {
      cb.setLastUpdateTime(normalizedTimestamp);
    }

    // Extract last fill from negative size changes (fills appear as reductions)
    const neg = (obj.changes || [])
      .map((c: any) => {
        const oldVal = Number.isFinite(c.old) ? Number(c.old) : Number.isFinite(c.previous) ? Number(c.previous) : Number.isFinite(c.prev) ? Number(c.prev) : NaN;
        const newVal = Number.isFinite(c.new) ? Number(c.new) : Number.isFinite(c.size) ? Number(c.size) : NaN;
        if (!Number.isFinite(oldVal) || !Number.isFinite(newVal) || oldVal === newVal) return null;
        const delta = newVal - oldVal;
        if (delta >= 0) return null;
        return { side: c.side as 'BID' | 'ASK', price: Number(c.price), size: Math.abs(delta) };
      })
      .filter(Boolean) as Array<{ side: 'BID' | 'ASK'; price: number; size: number }>;

    if (neg.length > 0) {
      const lastNeg = neg[0];
      const side = lastNeg.side === 'BID' ? 'buy' : 'sell';
      cb.lastFillBySecIdRef.current.set(normalizedSecId, { side, price: lastNeg.price, size: lastNeg.size, time: obj.timestamp });
    }
  }
  return true;
}
